import { Injectable, Injector } from "@angular/core";
import { map, Observable, of } from "rxjs";

import { ContentPipe } from "src/app/services/content/content.pipe";
import { RoutesManager } from "src/app/shared/models/routes.interfaces";
import { AssessmentsService } from "./assessments.service";
import { AssessmentUseCaseEnum } from "../models/assessments.enum";
import { AssessmentTemplateEnum } from "src/app/api/models/assessments/assessment.enum";
import { AssessmentsTemplate } from "src/app/api/models/assessments/assessment-template.interface";
import { AssessmentCustomLabel, AssessmentTag } from "src/app/api/models/assessments/assessment.interface";

@Injectable({
    providedIn: 'root'
})
export class AssessmentsSplitByUseCaseService {

    constructor(
        private injector: Injector,
        private contentPipe: ContentPipe,
    ) {}

    private _splitByUseCaseMapping: Record<string, AssessmentTemplateEnum[]>;

    /**
     * The `ai-assessments.splitByUseCase` CMS map, resolved lazily on first use. It must NOT be read
     * in a field initializer / constructor: this root service can be constructed during app/module
     * init (e.g. RisksService → RiskRegistryTableStore → AssessmentsService → AiAssessmentsStore →
     * here) before the `ai-assessments` content is loaded, which made ContentPipe throw on undefined
     * schema and cascade into an NG0200. Deferring to first method call guarantees content is ready.
     */
    private get splitByUseCaseMapping(): Record<string, AssessmentTemplateEnum[]> {
        return this._splitByUseCaseMapping ??=
            this.contentPipe.transform('ai-assessments.splitByUseCase') as Record<string, AssessmentTemplateEnum[]>;
    }

    selectUseCaseByTemplateType(type: AssessmentTemplateEnum): AssessmentUseCaseEnum {
        const mapping = this.splitByUseCaseMapping;
        if (!mapping) {
            return null; // ai-assessments content not loaded — degrade gracefully instead of crashing
        }
        for (let useCase of Object.values(AssessmentUseCaseEnum)) {
            if (mapping[useCase]?.includes(type)) {
                return useCase;
            }
        }
        return null;
    }

    selectAssessmentsRoute(useCase: AssessmentUseCaseEnum): string {
        // Unified Assessments collapses the per-use-case routes into the single `/assessments` table
        // (RoPA keeps its own `/ropa-assessments`). There is no `/ai-assessments`,
        // `/compliance-assessments` or `/vendor-assessments`.
        return useCase === AssessmentUseCaseEnum.Ropa
            ? RoutesManager.ropa_assessments
            : RoutesManager.assessments;
    }

    selectTemplatesRoute(useCase: AssessmentUseCaseEnum): Observable<string[]> {
        switch (useCase) {
            case AssessmentUseCaseEnum.AI:
                return of([this.selectAssessmentsRoute(useCase), RoutesManager.assessments_templates]);
            case AssessmentUseCaseEnum.Privacy:
                return of([this.selectAssessmentsRoute(useCase), RoutesManager.assessments_templates]);
            case AssessmentUseCaseEnum.Ropa:
                return this.selectPATemplate().pipe(
                    map(template => ([this.selectAssessmentsRoute(useCase), RoutesManager.assessments_templates, template.id?.toString()]))
                );
            case AssessmentUseCaseEnum.Vendor:
                return of([this.selectAssessmentsRoute(useCase), RoutesManager.assessments_templates]);
            default:
                return of([this.selectAssessmentsRoute(useCase), RoutesManager.assessments_templates]);
        }
    }
    
    isUseCaseSupportsTemplateType(useCase: AssessmentUseCaseEnum, templateType: AssessmentTemplateEnum): boolean {
        return this.splitByUseCaseMapping?.[useCase]?.includes(templateType) ?? false;
    }

    /**
     * Whether a template belongs to a use-case tab under Unified Assessments.
     *
     * Prefers the migrated labels (contract §2): a template belongs to the tab when its labels
     * include the use-case label (`Privacy` / `Vendor` / `AI`, which equal the `AssessmentUseCaseEnum`
     * values). Falls back to the legacy type→use-case heuristic when the template carries no use-case
     * label yet (e.g. MineOS OOB templates pending Squidex tagging), so there is no regression while
     * template tagging rolls out.
     */
    isTemplateInUseCase(
        useCase: AssessmentUseCaseEnum,
        templateType: AssessmentTemplateEnum,
        tags?: AssessmentCustomLabel[] | AssessmentTag[] | string[]
    ): boolean {
        const useCaseLabels = this.extractUseCaseLabelNames(tags);
        // PA stays type-driven (see `selectUseCaseByLabelsOrType`): a label must not put the
        // processing-activity template in a Privacy/Vendor/AI gallery tab it can't create from.
        if (useCaseLabels.length && !this.isProcessingActivityType(templateType)) {
            return useCaseLabels.includes(useCase);
        }
        return this.isUseCaseSupportsTemplateType(useCase, templateType);
    }

    /**
     * The use-case a template/assessment belongs to for tab grouping. Prefers the migrated
     * use-case label (contract §1/§2) when a use-case label is present; otherwise falls back to the
     * legacy type→use-case heuristic — zero regression while
     * the type→label migration + template tagging roll out. Mirror of `isTemplateInUseCase`, but
     * returns the single grouping key rather than testing membership.
     */
    selectUseCaseByLabelsOrType(
        type: AssessmentTemplateEnum,
        tags?: AssessmentCustomLabel[] | AssessmentTag[] | string[]
    ): AssessmentUseCaseEnum {
        const [useCaseLabel] = this.extractUseCaseLabelNames(tags);
        // A PA IS a processing activity, so it stays type-driven (→ RoPA) no matter what labels it
        // carries. A PA labelled `Vendor`/`Privacy`/`AI` — a pre-existing customer label whose name
        // collides with a seeded one, or a stray migration tag — would otherwise be bucketed into
        // that tab, and the unified table then drops it again BY TYPE
        // (`excludeProcessingActivitiesWhenUnified`), so it disappears from RoPA *and* from the tab.
        if (useCaseLabel && !this.isProcessingActivityType(type)) {
            return useCaseLabel;
        }
        return this.selectUseCaseByTemplateType(type);
    }

    /** PA is the one type the label preference must not override — it defines the RoPA world. */
    private isProcessingActivityType(type: AssessmentTemplateEnum): boolean {
        return type === AssessmentTemplateEnum.PA;
    }

    /** The subset of a template's label names that are use-case labels (Privacy / Vendor / AI). */
    private extractUseCaseLabelNames(tags?: AssessmentCustomLabel[] | AssessmentTag[] | string[]): AssessmentUseCaseEnum[] {
        const useCaseValues = Object.values(AssessmentUseCaseEnum) as string[];
        return (tags ?? [])
            .map(tag => (typeof tag === 'string' ? tag : tag?.name))
            .filter((name): name is string => !!name && useCaseValues.includes(name)) as AssessmentUseCaseEnum[];
    }

    selectPATemplate(): Observable<AssessmentsTemplate> {
        return this.injector.get(AssessmentsService).selectPaTemplate();
    }

    getAiTemplateTypes(): AssessmentTemplateEnum[] {
        return this.splitByUseCaseMapping?.[AssessmentUseCaseEnum.AI] ?? [];
    }
}